/**
 * envTitlePrefix — guarantees every browser-tab title in non-production
 * environments starts with a clear environment tag (`[LOCAL]` / `[STAGING]`)
 * so engineers can tell tabs apart at a glance. Production tabs render
 * unchanged.
 *
 * ── Detection strategy ──
 * Detection is based on `window.location.hostname`, NOT `process.env.*`.
 * Reason: process.env values get baked into the bundle by webpack at build
 * time, and CRA's babel-loader / persistent webpack 5 cache can serve a
 * stale compiled version where the env-var checks already evaluated to
 * something else. The hostname is read fresh by the browser on every page
 * load, so it cannot be poisoned by stale build output.
 *
 *   localhost / 127.0.0.1 / *.local           → [LOCAL]
 *   any *.prodactivelabs.com                   → [STAGING]
 *   any getsmarthub.co (or anything else)      → no prefix
 *
 * Why a MutationObserver?
 *   The app sets document.title from many places — index.js bootstrap, the
 *   usePageMeta hook (per route), product subviews that swap the title on
 *   the fly, etc. Wrapping every call site is brittle and easy to forget.
 *   A single observer on <title> ensures the prefix is re-applied whenever
 *   ANY code path mutates the title, with zero awareness required at the
 *   call sites.
 */

function detectEnvPrefix() {
  if (typeof window === 'undefined' || !window.location) return '';
  const host = (window.location.hostname || '').toLowerCase();
  if (
    host === 'localhost'
    || host === '127.0.0.1'
    || host === '0.0.0.0'
    || host === '[::1]'
    || host.endsWith('.local')
    || host.endsWith('.localhost')
  ) {
    return '[LOCAL] ';
  }
  if (host.endsWith('.prodactivelabs.com') || host === 'prodactivelabs.com') {
    return '[STAGING] ';
  }
  // getsmarthub.co (and anything else, e.g. preview/CDN URLs) → no prefix
  return '';
}

const ENV_PREFIX = detectEnvPrefix();

let installed = false;
// Re-entrancy guard: when the observer rewrites the title we don't want to
// fire again on our own mutation.
let internalUpdate = false;

function applyPrefix() {
  if (internalUpdate) return;
  if (!ENV_PREFIX) return;
  const current = document.title || '';
  if (current.startsWith(ENV_PREFIX)) return;
  internalUpdate = true;
  try {
    document.title = ENV_PREFIX + current;
  } finally {
    internalUpdate = false;
  }
}

/**
 * Install the title-prefix observer. Idempotent and safe to call multiple
 * times. Intended to be invoked once at app bootstrap (src/index.js).
 *
 * On production hosts (no prefix) this is a no-op.
 */
export function installEnvTitlePrefix() {
  if (installed) return;
  if (typeof document === 'undefined') return;

  installed = true;

  if (!ENV_PREFIX) return;

  // Apply once for the title baked into index.html / set by index.js bootstrap.
  applyPrefix();

  if (typeof MutationObserver === 'undefined') return;

  const titleEl = document.querySelector('head > title');
  if (titleEl) {
    // Watch text-node changes inside <title> — this is what `document.title = …`
    // produces under the hood.
    new MutationObserver(applyPrefix).observe(titleEl, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  // Some libraries replace the entire <title> element (e.g. react-helmet);
  // also watch <head> for added/removed children so we re-apply the prefix
  // when that happens.
  if (document.head) {
    new MutationObserver(applyPrefix).observe(document.head, { childList: true });
  }
}

/**
 * Returns the active environment prefix (empty string in production). Useful
 * if a caller wants to read the same value rather than reproduce the rules.
 */
export function getEnvTitlePrefix() {
  return ENV_PREFIX;
}
